import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import { ShortlistSort, ShortlistSortOrder, ShortlistSortProfileValue } from '@modules/shortlist/types'

type SortProfileValuesMap = Record<
  string,
  {
    label: string
    order: ShortlistSortOrder
    sort: ShortlistSort
    addCompanyIdsToRequest?: boolean
  }
>

export const DEFAULT_SHORTLIST_API_SORT = { field: 'createdAt', order: 'asc' }

export const MAP_FIELD_NAME_TO_API_NAME: Record<string, string> = {
  company: 'companyName',
  contactStatus: 'statusType',
  futureEvents: 'futureEventEndDate',
  lastEvent: 'pastEventEndDate',
  note: 'noteUpdatedAt',
}

/** @deprecated */
export const useSortProfileValuesMap = (): SortProfileValuesMap => {
  const { t } = useTranslation()

  const sortProfileValuesMap: SortProfileValuesMap = useMemo<SortProfileValuesMap>(
    () => ({
      [ShortlistSortProfileValue.ASSET_COMPANY_TITLE]: {
        label: t(translationKeys.ASSET_COMPANY_TITLE),
        order: ShortlistSortOrder.NOT_ASC_DESC,
        sort: { fieldName: 'text', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.COMPANY_NAME_ASC]: {
        label: t(translationKeys.COMPANY_NAME_ASC),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'companyName', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.COMPANY_NAME_DESC]: {
        label: t(translationKeys.COMPANY_NAME_DESC),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'companyName', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.DATE_ADDED_ASCENDING]: {
        label: t(translationKeys.DATE_ADDED_ASCENDING),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'createdAt', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.DATE_ADDED_DESCENDING]: {
        label: t(translationKeys.DATE_ADDED_DESCENDING),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'createdAt', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.NEWEST_EVENT]: {
        label: t(translationKeys.NEWEST_EVENT),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'eventStartDate', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.OLDEST_EVENT]: {
        label: t(translationKeys.OLDEST_EVENT),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'eventStartDate', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.MOST_RECENT_EVENT_FIRST]: {
        label: t(translationKeys.MOST_RECENT_EVENT_FIRST),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'pastEventEndDate', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.NEXT_EVENT]: {
        label: t(translationKeys.NEXT_EVENT),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'futureEventEndDate', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.STATUS_TYPE]: {
        label: t(translationKeys.STATUS_TYPE),
        order: ShortlistSortOrder.NOT_ASC_DESC,
        sort: { fieldName: 'statusType', sortOrder: ShortlistSortOrder.ASC },
        addCompanyIdsToRequest: true,
      },
      [ShortlistSortProfileValue.DATE_UPDATED_ASCENDING]: {
        label: t(translationKeys.DATE_UPDATED_ASCENDING),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'updatedAt', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.DATE_UPDATED_DESCENDING]: {
        label: t(translationKeys.DATE_UPDATED_DESCENDING),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'updatedAt', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.DONE]: {
        label: t(translationKeys.DONE),
        order: ShortlistSortOrder.NOT_ASC_DESC,
        sort: { fieldName: 'nda', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.NOT_DONE]: {
        label: t(translationKeys.NOT_DONE),
        order: ShortlistSortOrder.NOT_ASC_DESC,
        sort: { fieldName: 'nda', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.NEWEST_NOTE]: {
        label: t(translationKeys.NEWEST_NOTE),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'noteUpdatedAt', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.OLDEST_NOTE]: {
        label: t(translationKeys.OLDEST_NOTE),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'noteUpdatedAt', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.PRIORITY_HIGH_TO_NOT_SET]: {
        label: t(translationKeys.PRIORITY_HIGH_TO_NOT_SET),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'priority', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.PRIORITY_NOT_SET_TO_HIGH]: {
        label: t(translationKeys.PRIORITY_NOT_SET_TO_HIGH),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'priority', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.STATUS]: {
        label: t(translationKeys.STATUS),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'tagId', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.NEWEST_TEXTS]: {
        label: t(translationKeys.NEWEST_TEXTS),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'customFieldValues', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.OLDEST_TEXTS]: {
        label: t(translationKeys.OLDEST_TEXTS),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'customFieldValues', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.PROFILE_TITLE_TYPE]: {
        label: t(translationKeys.PROFILE_TITLE_TYPE),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'profileType', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.PROFILE_TITLE_ASC]: {
        label: t(translationKeys.PROFILE_TITLE_ASC),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'text', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.PROFILE_TITLE_DESC]: {
        label: t(translationKeys.PROFILE_TITLE_DESC),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'text', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.ALERT_DATE_ADDED_ASCENDING]: {
        label: t(translationKeys.ALERT_DATE_ADDED_ASCENDING),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'createdAt', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.ALERT_DATE_ADDED_DESCENDING]: {
        label: t(translationKeys.ALERT_DATE_ADDED_DESCENDING),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'createdAt', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.ALERT_DATE_UPDATE_ASCENDING]: {
        label: t(translationKeys.ALERT_DATE_UPDATE_ASCENDING),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'alertUpdatedAt', sortOrder: ShortlistSortOrder.ASC },
      },
      [ShortlistSortProfileValue.ALERT_DATE_UPDATE_DESCENDING]: {
        label: t(translationKeys.ALERT_DATE_UPDATE_DESCENDING),
        order: ShortlistSortOrder.DESC,
        sort: { fieldName: 'alertUpdatedAt', sortOrder: ShortlistSortOrder.DESC },
      },
      [ShortlistSortProfileValue.ALERT_FREQUENCY]: {
        label: t(translationKeys.ALERT_FREQUENCY),
        order: ShortlistSortOrder.ASC,
        sort: { fieldName: 'alertFrequency', sortOrder: ShortlistSortOrder.ASC },
      },
    }),
    [t],
  )

  return sortProfileValuesMap
}
